import type {TInfiniteQueryOptions, TQueryResultWithCount} from "@/store/types";
import {
  useInfiniteQuery,
  useQuery,
  type QueryKey,
  type UseInfiniteQueryResult,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {AxiosError, AxiosResponse} from "axios";

const useGenericInfiniteQuery = <TQueryFnData, TError = AxiosError, TData = TQueryFnData[]>(
  options: TInfiniteQueryOptions<TQueryFnData, TError, TData> & {
    queryKey: QueryKey;
  },
): UseInfiniteQueryResult<TData, TError> =>
  useInfiniteQuery({
    initialPageParam: options.queryKey[0],
    getNextPageParam: lastPage => lastPage.data.next,
    select: response => response.pages.flatMap(page => page.data.results) as unknown as TData,
    ...options,
  });

const useGenericInfiniteQueryWithCount = <TQueryFnData, TError = AxiosError, TData = TQueryFnData[]>(
  options: TInfiniteQueryOptions<TQueryFnData, TError, TQueryResultWithCount<TData>> & {
    queryKey: QueryKey;
  },
): UseInfiniteQueryResult<TQueryResultWithCount<TData>, TError> =>
  useGenericInfiniteQuery<TQueryFnData, TError, TQueryResultWithCount<TData>>({
    select: response => ({
      count: response.pages[0].data.count,
      results: response.pages.flatMap(page => page.data.results) as unknown as TData,
    }),
    ...options,
  });

const useGenericQuery = <TQueryFnData, TError = AxiosError, TData = TQueryFnData>(
  options: UseQueryOptions<AxiosResponse<TQueryFnData>, TError, TData>,
): UseQueryResult<TData, TError> =>
  useQuery({
    select: response => response.data as unknown as TData,
    ...options,
  });

export {useGenericInfiniteQuery, useGenericInfiniteQueryWithCount, useGenericQuery};
